import { Button, TabBar } from 'components';
import { useHashState } from 'hooks/useHash';
import { CopyIcon } from 'lib/svgs/Icons';
import { useTranslations } from 'next-intl';
import { FC, useCallback, useMemo } from 'react';
import { KeysEnum } from 'types/KeysEnum';
import { useContentGenerationContext } from '../_context/ContentGenerationContext';
import ContentBriefMenu from './ContentBriefMenu';

interface IMenuContentGeneration {
  onCancel: () => void;
  onReset?: () => void;
}

const MenuContentGeneration: FC<IMenuContentGeneration> = ({ onCancel, onReset }) => {
  const t = useTranslations();
  const [hash, setHash] = useHashState();

  const { handleDuplicate } = useContentGenerationContext();

  const tabsNavigation = useCallback((hashName: string) => () => setHash(hashName), [setHash]);

  const handleResetDuplicate = useCallback(
    () => onReset?.() ?? handleDuplicate?.(),
    [onReset, handleDuplicate]
  );

  const tabs = useMemo(
    () =>
      [
        {
          label: t('content_generation.tabs.content_brief'),
          hash: KeysEnum.ContentBrief
        }
      ].map((tab) => ({
        ...tab,
        onClick: tabsNavigation(tab.hash)
      })),
    [t, tabsNavigation]
  );

  const activeTab = useMemo(() => {
    const tabIndex = tabs.findIndex((tab) => tab.hash === hash) ?? 0;

    return tabIndex !== -1 ? tabIndex : 0;
  }, [hash, tabs]);

  const screens = [<ContentBriefMenu key={KeysEnum.ContentBrief} />];

  return (
    <div className="relative z-[999] flex h-full flex-1 flex-col bg-white shadow-[0px_2px_8px_0px_rgba(36,92,144,0.17),0_8px_8px_-8px_rgba(36,92,144,0.17)_inset]">
      <TabBar
        tabs={tabs}
        activeTabIndex={activeTab}
        scaleVariant="4"
        addPadding
        smallGap
        className="mt-4 flex flex-1 flex-col overflow-y-auto"
      >
        {({ selectedIndex }) => (
          <div className="flex h-full flex-1 flex-col overflow-y-scroll">
            {screens[selectedIndex]}
          </div>
        )}
      </TabBar>
      <div className="absolute right-4 top-3">
        <div
          data-tooltip-id="tolq-tooltip"
          data-tooltip-content={t('tooltips.create_new_content') ?? t('tooltips.duplicate_content')}
        >
          <Button
            variant="text"
            size="small"
            startAdornment={<CopyIcon />}
            className="!p-0"
            label={t('style_guides.card.duplicate') ?? t('buttons.duplicate')}
            onClick={handleResetDuplicate}
          />
        </div>
      </div>
    </div>
  );
};

export default MenuContentGeneration;
